import { Mail } from 'lucide-react';
import type {
  LBNodeData,
  TeamNotifRecipients,
} from '@/proto/logiblocks/logiblocks';
import { NOTIFICATIONS_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import {
  collectErrors,
  type ValidationError,
  validateRequiredArray,
  validateRequiredTemplate,
} from '../../fieldValidation';
import { validateTemplateChunks } from '../../validation';
import { MSG_INVALID_VALUE } from '../../validationMessages';
import { EmailActionCustomization } from './EmailActionCustomization';
import { buildNodeData } from '../../nodeDataUtils';

/** Validation field names for the Email action. Shared with the UI. */
export const EmailActionFields = {
  RECIPIENTS: 'Recipients',
  SUBJECT: 'Subject',
  BODY: 'Body',
  SEND_MODE: 'Send mode',
} as const;

/**
 * How an Email action delivers to its recipients: one copy each, or a single
 * shared email. These strings are serialized to the proto and must match the
 * backend values in `email_action_node.py`.
 */
export const EmailActionSendMode = {
  SEPARATE: 'separate',
  SHARED: 'shared',
} as const;

export type EmailActionSendMode =
  (typeof EmailActionSendMode)[keyof typeof EmailActionSendMode];

const VALID_SEND_MODES: ReadonlySet<string> = new Set(
  Object.values(EmailActionSendMode)
);

/** Flag a send mode that isn't one of the values the backend understands. */
function validateSendMode(
  sendMode: string | undefined,
  fieldName: string
): ValidationError | undefined {
  if (sendMode && !VALID_SEND_MODES.has(sendMode)) {
    return {
      type: 'VALUE_ERROR',
      field: fieldName,
      message: MSG_INVALID_VALUE,
    };
  }
  return undefined;
}

type RecipientWithLegacy = TeamNotifRecipients & {
  additional_recipients_ids?: string[];
  additionalRecipientsIds?: string[];
  is_additional?: boolean;
};

export const EmailActionNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'email_action',

  category: NodeCategory.NOTIFICATIONS,

  label: 'Email',

  hasInput: true,

  getIcon: (size: number) => (
    <Mail size={size} color={NOTIFICATIONS_ICON_COLOR} />
  ),

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, _graph: GraphData) => {
    const to = data.email_action?.to;
    if (!to || to.length === 0) {
      return 'To: (no recipients)';
    }

    const teamCount = to.filter(
      (r) =>
        r.select_all &&
        !r.is_additional &&
        !(r as RecipientWithLegacy).is_additional
    ).length;

    const userCount = to
      .filter(
        (r) =>
          !r.select_all &&
          !r.is_additional &&
          !(r as RecipientWithLegacy).is_additional
      )
      .reduce((sum, r) => sum + (r.user_ids?.length || 0), 0);

    const additionalCount = to.reduce((sum, r) => {
      const rec = r as RecipientWithLegacy;
      const add =
        rec.additional_recipients ||
        rec.additional_recipients_ids ||
        rec.additionalRecipientsIds ||
        [];
      return sum + add.length;
    }, 0);

    const parts: string[] = [];
    if (teamCount > 0) {
      parts.push(`${teamCount} team${teamCount === 1 ? '' : 's'}`);
    }
    if (userCount > 0) {
      parts.push(`${userCount} user${userCount === 1 ? '' : 's'}`);
    }
    if (additionalCount > 0) {
      parts.push(
        `${additionalCount} primary contact${additionalCount === 1 ? '' : 's'}`
      );
    }

    if (parts.length === 0) {
      return 'To: (no recipients)';
    }

    return `To: ${parts.join(', ')}`;
  },

  getValidationErrors: (
    nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const email = nodeData.email_action;
    return collectErrors(
      validateRequiredArray(email?.to, EmailActionFields.RECIPIENTS),
      validateRequiredTemplate(
        email?.subject_chunks ?? [],
        EmailActionFields.SUBJECT
      ),
      validateRequiredTemplate(
        email?.body_chunks ?? [],
        EmailActionFields.BODY
      ),
      validateSendMode(email?.send_mode, EmailActionFields.SEND_MODE),
      ...validateTemplateChunks(
        email?.subject_chunks ?? [],
        nodeId,
        graph,
        EmailActionFields.SUBJECT
      ),
      ...validateTemplateChunks(
        email?.body_chunks ?? [],
        nodeId,
        graph,
        EmailActionFields.BODY
      )
    );
  },

  CustomizationComponent: EmailActionCustomization,

  createDefaultNodeData: () =>
    buildNodeData('email_action', {
      to: [],
      cc: [],
      bcc: [],
      send_mode: EmailActionSendMode.SEPARATE,
      subject_chunks: [],
      body_chunks: [],
    }),

  showCustomizePanelOnCreation: true,
};
